import { Tool, categoryColors } from '@/data/tools';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { ToolIcon } from './tools/ToolIcon';

interface ToolCardProps {
  tool: Tool;
  index: number;
}

const getCategoryClasses = (category: string) => {
  const cat = category?.toLowerCase() || '';
  if (cat.includes('converter')) {
    return {
      icon: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 ring-indigo-100 dark:ring-indigo-500/20',
      textHover: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
      ringHover: 'hover:ring-indigo-500/15'
    };
  }
  if (cat.includes('dev') || cat.includes('code')) {
    return {
      icon: 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400 ring-orange-100 dark:ring-orange-500/20',
      textHover: 'group-hover:text-orange-600 dark:group-hover:text-orange-400',
      ringHover: 'hover:ring-orange-500/15'
    };
  }
  if (cat.includes('text')) {
    return {
      icon: 'bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400 ring-pink-100 dark:ring-pink-500/20',
      textHover: 'group-hover:text-pink-600 dark:group-hover:text-pink-400',
      ringHover: 'hover:ring-pink-500/15'
    };
  }
  if (cat.includes('image') || cat.includes('media')) {
    return {
      icon: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 ring-emerald-100 dark:ring-emerald-500/20',
      textHover: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
      ringHover: 'hover:ring-emerald-500/15'
    };
  }
  if (cat.includes('pdf')) {
    return {
      icon: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-100 dark:ring-rose-500/20',
      textHover: 'group-hover:text-rose-600 dark:group-hover:text-rose-400',
      ringHover: 'hover:ring-rose-500/15'
    };
  }
  if (cat.includes('calculator')) {
    return {
      icon: 'bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400 ring-teal-100 dark:ring-teal-500/20',
      textHover: 'group-hover:text-teal-600 dark:group-hover:text-teal-400',
      ringHover: 'hover:ring-teal-500/15'
    };
  }
  if (cat.includes('games') || cat.includes('science')) {
    return {
      icon: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400 ring-amber-100 dark:ring-amber-500/20',
      textHover: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
      ringHover: 'hover:ring-amber-500/15'
    };
  }
  if (cat.includes('seo') || cat.includes('network')) {
    return {
      icon: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400 ring-violet-100 dark:ring-violet-500/20',
      textHover: 'group-hover:text-violet-600 dark:group-hover:text-violet-400',
      ringHover: 'hover:ring-violet-500/15'
    };
  }
  if (cat.includes('design')) {
    return {
      icon: 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400 ring-sky-100 dark:ring-sky-500/20',
      textHover: 'group-hover:text-sky-600 dark:group-hover:text-sky-400',
      ringHover: 'hover:ring-sky-500/15'
    };
  }
  return {
    icon: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 ring-blue-100 dark:ring-blue-500/20',
    textHover: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
    ringHover: 'hover:ring-blue-500/15'
  };
};

const ToolCard = memo(function ToolCard({ tool, index }: ToolCardProps) {
  const classes = getCategoryClasses(tool.category);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.35, delay: index * 0.03, ease: 'easeOut' }}
      whileHover={{ y: -5, transition: { duration: 0.15 } }}
      className="h-full"
    >
      <Link
        to={tool.route}
        className={`group relative bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full overflow-hidden theme-transition ring-1 ring-transparent ${classes.ringHover}`}
      >
        <div className="absolute top-0 right-0 p-5 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0 duration-300">
          <ArrowUpRight size={20} className="text-gray-400 dark:text-gray-600" />
        </div>

        <div className="mb-4 relative z-10">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className={`
              w-12 h-12 rounded-xl flex items-center justify-center
              transition-transform duration-300 ease-out
              ring-1 ${classes.icon}
            `}>
            <ToolIcon icon={tool.icon} size={24} />
          </motion.div>
        </div>

        <div className="relative z-10 flex-grow">
          <h3 className={`font-bold text-base text-slate-800 dark:text-slate-100 ${classes.textHover} transition-colors mb-2 leading-tight tracking-tight`}>
            {tool.name}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed font-normal">
            {tool.description}
          </p>
        </div>
      </Link>
    </motion.div>
  );
});

export default ToolCard;
